import React, { useState } from "react";
import type {
  AccessRequestFormData,
  IndustryOption,
} from "@/types/accessRequest";
import { INDUSTRY_OPTIONS } from "@/types/accessRequest";

interface RequestAccessFormProps {
  onSubmit: (data: AccessRequestFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}

export const RequestAccessForm: React.FC<RequestAccessFormProps> = ({
  onSubmit,
  onCancel,
  isSubmitting,
}) => {
  const [formData, setFormData] = useState<AccessRequestFormData>({
    fullName: "",
    email: "",
    company: "",
    industry: "",
    reason: "",
    phone: "",
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof AccessRequestFormData, string>>
  >({});

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof AccessRequestFormData, string>> = {};

    // Required fields validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.industry) {
      newErrors.industry = "Please select an industry";
    }

    if (!formData.reason.trim()) {
      newErrors.reason = "Reason for access is required";
    } else if (formData.reason.trim().length < 20) {
      newErrors.reason = "Please provide at least 20 characters";
    }

    // Optional phone validation (if provided)
    if (formData.phone && formData.phone.trim()) {
      const phoneRegex =
        /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
      if (!phoneRegex.test(formData.phone.replace(/\s/g, ""))) {
        newErrors.phone = "Please enter a valid phone number";
      }
    }



    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      // Error handling is done in parent component
      console.error("Form submission error:", error);
    }
  };

  const handleChange = (field: keyof AccessRequestFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing/changing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };



  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Full Name */}
      <div>
        <label
          htmlFor="fullName"
          className="block text-sm font-medium text-gray-300 mb-2">
          Full Name <span className="text-red-400">*</span>
        </label>
        <input
          id="fullName"
          type="text"
          value={formData.fullName}
          onChange={(e) => handleChange("fullName", e.target.value)}
          className={`w-full px-4 py-3 bg-gray-700/50 border ${
            errors.fullName ? "border-red-500" : "border-gray-600"
          } rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all`}
          placeholder="John Doe"
          disabled={isSubmitting}
        />
        {errors.fullName && (
          <p className="mt-1 text-sm text-red-400">{errors.fullName}</p>
        )}
      </div>

      {/* Email */}
      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium text-gray-300 mb-2">
          Email Address <span className="text-red-400">*</span>
        </label>
        <input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => handleChange("email", e.target.value)}
          className={`w-full px-4 py-3 bg-gray-700/50 border ${
            errors.email ? "border-red-500" : "border-gray-600"
          } rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all`}
          placeholder="john@example.com"
          disabled={isSubmitting}
        />
        {errors.email && (
          <p className="mt-1 text-sm text-red-400">{errors.email}</p>
        )}
      </div>

      {/* Company (Optional) */}
      <div>
        <label
          htmlFor="company"
          className="block text-sm font-medium text-gray-300 mb-2">
          Company <span className="text-gray-500 text-xs">(Optional)</span>
        </label>
        <input
          id="company"
          type="text"
          value={formData.company}
          onChange={(e) => handleChange("company", e.target.value)}
          className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
          placeholder="Acme Inc."
          disabled={isSubmitting}
        />
      </div>

      {/* Industry Dropdown */}
      <div>
        <label
          htmlFor="industry"
          className="block text-sm font-medium text-gray-300 mb-2">
          Industry <span className="text-red-400">*</span>
        </label>
        <select
          id="industry"
          value={formData.industry}
          onChange={(e) =>
            handleChange("industry", e.target.value as IndustryOption)
          }
          className={`w-full px-4 py-3 bg-gray-700/50 border ${
            errors.industry ? "border-red-500" : "border-gray-600"
          } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all appearance-none cursor-pointer`}
          disabled={isSubmitting}>
          <option value="" className="bg-gray-800">
            Select your industry
          </option>
          {INDUSTRY_OPTIONS.map((option) => (
            <option key={option} value={option} className="bg-gray-800">
              {option}
            </option>
          ))}
        </select>
        {errors.industry && (
          <p className="mt-1 text-sm text-red-400">{errors.industry}</p>
        )}
      </div>

      {/* Reason for Access */}
      <div>
        <label
          htmlFor="reason"
          className="block text-sm font-medium text-gray-300 mb-2">
          Why are you requesting access? <span className="text-red-400">*</span>
        </label>
        <textarea
          id="reason"
          value={formData.reason}
          onChange={(e) => handleChange("reason", e.target.value)}
          rows={3}
          maxLength={500}
          className={`w-full px-4 py-3 bg-gray-700/50 border ${
            errors.reason ? "border-red-500" : "border-gray-600"
          } rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none`}
          placeholder="Tell us why you're interested in Hermes Terminal..."
          disabled={isSubmitting}
        />
        <p className="mt-1 text-xs text-gray-400">
          {formData.reason.length}/500 characters (minimum 20)
        </p>
        {errors.reason && (
          <p className="mt-1 text-sm text-red-400">{errors.reason}</p>
        )}
      </div>

      {/* Phone Number (Optional with Privacy Notice) */}
      <div>
        <label
          htmlFor="phone"
          className="block text-sm font-medium text-gray-300 mb-2">
          Phone Number <span className="text-gray-500 text-xs">(Optional)</span>
        </label>
        <input
          id="phone"
          type="tel"
          value={formData.phone}
          onChange={(e) => handleChange("phone", e.target.value)}
          className={`w-full px-4 py-3 bg-gray-700/50 border ${
            errors.phone ? "border-red-500" : "border-gray-600"
          } rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all`}
          placeholder="+1 (555) 123-4567"
          disabled={isSubmitting}
        />
        <p className="mt-1 text-xs text-gray-400 flex items-start gap-1">
          <svg
            className="w-3 h-3 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <span>
            We respect your privacy. Your phone number is optional and will only
            be used for important account-related communications if necessary.
          </span>
        </p>
        {errors.phone && (
          <p className="mt-1 text-sm text-red-400">{errors.phone}</p>
        )}
      </div>



      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="w-full sm:flex-1 py-3 px-6 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed">
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full sm:flex-1 py-3 px-6 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
          {isSubmitting ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Submitting...</span>
            </>
          ) : (
            "Submit Request"
          )}
        </button>
      </div>


    </form>
  );
};
